import {useSwiperSlide} from 'swiper/react';
import {CustomerFieldKeys, getValue} from '../../../support/get_data';
import {useAppSelector} from '../../../store/store';
import {useIsNavigatingOrPending} from '../../../support/use_is_navigating_or_pending';
import formStyles from '../../../components/form/form.module.scss';
import mapStyles from '../../../components/map/map.module.scss';
import {Button} from '../../../components/button/button';
import {__, transString} from '../../../support/lang/translations';
import {ButtonColorType} from '../../../enums/button_color_type.ts';
import {classNames} from '../../../support/classnames.ts';
import {CountrySelect} from '../../../components/country_select/country_select.tsx';
import {Map} from '../../../components/map/map.tsx';
import {useState} from 'react';
import buttonStyles from '../../../components/button/button.module.scss';

export function LocationDetailsTitle() {
    const meInfo = useAppSelector((state) => state.customerInfo.me);
    const meData = meInfo?.customer;

    const streetData = getValue(meData, CustomerFieldKeys.STREET);
    const houseNumberData = getValue(meData, CustomerFieldKeys.HOUSE_NUMBER);
    const houseNumberAdditionData = getValue(meData, CustomerFieldKeys.HOUSE_NUMBER_ADDITION);

    let address: string | null = null;
    if (streetData?.value && houseNumberData?.value) {
        address = `${streetData?.value} ${houseNumberData?.value + (houseNumberAdditionData?.value ?? '')}`;
    }

    return (
        <>
            <h1>{__('onboarding.location_details.title')}</h1>
            <h2>
                {address !== null
                    ? __('onboarding.location_details.subtitle_address', {address})
                    : __('onboarding.location_details.subtitle_unknown')}
            </h2>
        </>
    );
}

interface ContentProps {
    isEditing: boolean;
    onEditClick: () => void;
}
export function LocationDetailsContent(props: ContentProps) {
    const isLoading = useIsNavigatingOrPending((state) => state.customerInfo.status);

    const meInfo = useAppSelector((state) => state.customerInfo.me);
    const meData = meInfo?.customer;

    const streetData = getValue(meData, CustomerFieldKeys.STREET);
    const houseNumberData = getValue(meData, CustomerFieldKeys.HOUSE_NUMBER);
    const houseNumberAdditionData = getValue(meData, CustomerFieldKeys.HOUSE_NUMBER_ADDITION);

    const postalCodeData = getValue(meData, CustomerFieldKeys.POSTAL_CODE);
    const cityData = getValue(meData, CustomerFieldKeys.CITY);
    const countryData = getValue(meData, CustomerFieldKeys.COUNTRY);

    const swiperSlide = useSwiperSlide();

    const [street, setStreet] = useState(`${streetData?.value}` ?? '');
    const [houseNumber, setHouseNumber] = useState(`${houseNumberData?.value}` ?? '');
    const [houseNumberAddition, setHouseNumberAddition] = useState(`${houseNumberAdditionData?.value ?? ''}`);
    const [postalCode, setPostalCode] = useState(`${postalCodeData?.value}` ?? '');
    const [city, setCity] = useState(`${cityData?.value}` ?? '');
    const [country, setCountry] = useState(`${countryData?.value}` ?? '');

    const [addressData, setUpdatedAddress] = useState(
        `${street} ${houseNumber + (houseNumberAddition ?? '')} ${postalCode} ${city} ${country}`,
    );

    function updateAddress() {
        if (street === '' || houseNumber === '' || postalCode === '' || city === '' || country === '') {
            setUpdatedAddress('');
        } else {
            setUpdatedAddress(
                `${street} ${houseNumber + (houseNumberAddition ?? '')} ${postalCode} ${city} ${country}`,
            );
        }
    }

    return (
        <>
            <h2>{__('onboarding.location_details.content_title')}</h2>

            <div className={formStyles.form} onClick={() => props.onEditClick()}>
                <div className={formStyles.form__grid}>
                    <div className={formStyles.grid__custom}>
                        <fieldset>
                            <legend data-grid-column-start={1} data-grid-column-end={13}>
                                {__('onboarding.location_details.fields.address')}
                            </legend>
                            <input
                                className={classNames({[formStyles.locked]: !props.isEditing})}
                                data-grid-column-start={1}
                                data-grid-column-end={7}
                                defaultValue={streetData?.value ?? ''}
                                placeholder={transString('onboarding.location_details.fields.street')}
                                name={CustomerFieldKeys.STREET}
                                type="text"
                                required={true}
                                disabled={isLoading}
                                onChange={(e) => {
                                    setStreet(e.target.value);
                                }}
                                onBlur={() => {
                                    updateAddress();
                                }}
                            />
                            <input
                                className={classNames({[formStyles.locked]: !props.isEditing})}
                                data-grid-column-start={7}
                                data-grid-column-end={10}
                                defaultValue={houseNumberData?.value ?? ''}
                                placeholder={transString('onboarding.location_details.fields.house_number')}
                                name={CustomerFieldKeys.HOUSE_NUMBER}
                                type="text"
                                required={true}
                                disabled={isLoading}
                                onChange={(e) => {
                                    setHouseNumber(e.target.value);
                                }}
                                onBlur={() => {
                                    updateAddress();
                                }}
                            />
                            <input
                                className={classNames({[formStyles.locked]: !props.isEditing})}
                                data-grid-column-start={10}
                                data-grid-column-end={13}
                                defaultValue={houseNumberAdditionData?.value ?? ''}
                                placeholder={transString('onboarding.location_details.fields.house_number_addition')}
                                name={CustomerFieldKeys.HOUSE_NUMBER_ADDITION}
                                type="text"
                                required={false}
                                disabled={isLoading}
                                onChange={(e) => {
                                    setHouseNumberAddition(e.target.value);
                                }}
                                onBlur={() => {
                                    updateAddress();
                                }}
                            />
                        </fieldset>
                    </div>
                    <div className={formStyles.grid__custom}>
                        <fieldset>
                            <legend data-grid-column-start={1} data-grid-column-end={13}>
                                {__('onboarding.location_details.fields.address_2')}
                            </legend>
                            <input
                                className={classNames({[formStyles.locked]: !props.isEditing})}
                                data-grid-column-start={1}
                                data-grid-column-end={7}
                                defaultValue={postalCodeData?.value ?? ''}
                                placeholder={transString('onboarding.location_details.fields.postal_code')}
                                name={CustomerFieldKeys.POSTAL_CODE}
                                type="text"
                                required={true}
                                disabled={isLoading}
                                onChange={(e) => {
                                    setPostalCode(e.target.value);
                                }}
                                onBlur={() => {
                                    updateAddress();
                                }}
                            />
                            <input
                                className={classNames({[formStyles.locked]: !props.isEditing})}
                                data-grid-column-start={7}
                                data-grid-column-end={13}
                                defaultValue={cityData?.value ?? ''}
                                placeholder={transString('onboarding.location_details.fields.city')}
                                name={CustomerFieldKeys.CITY}
                                type="text"
                                required={true}
                                disabled={isLoading}
                                onChange={(e) => {
                                    setCity(e.target.value);
                                }}
                                onBlur={() => {
                                    updateAddress();
                                }}
                            />
                        </fieldset>
                    </div>
                    <div className={formStyles.grid__default}>
                        <CountrySelect
                            isLoading={isLoading}
                            isLocked={!props.isEditing}
                            onChange={(e: string) => {
                                setCountry(e);
                                updateAddress();
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className={mapStyles.map_slide_wrapper}>
                {addressData !== '' ? <Map addressData={addressData} isVisible={swiperSlide.isActive} /> : null}
            </div>
        </>
    );
}

interface FooterProps {
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
    onPrevSlide: () => void;
    onNextSlide: () => void;
}

export function LocationDetailsFooter(props: FooterProps) {
    return (
        <>
            <div>
                {props.isEditing ? (
                    <span />
                ) : (
                    <div>
                        <Button
                            className={buttonStyles.button_step}
                            type="button"
                            onClick={() => props.setIsEditing(true)}
                            colorType={ButtonColorType.Black}
                        >
                            <i className="fa-light fa-x" />
                            <span>{__('onboarding.location_details.buttons.invalid')}</span>
                        </Button>
                    </div>
                )}
            </div>
            <div>
                <Button
                    className={buttonStyles.button_step}
                    type="button"
                    onClick={() => props.onNextSlide()}
                    colorType={ButtonColorType.Green}
                >
                    <i className="fa-light fa-check" />
                    {props.isEditing ? (
                        <span>{__('onboarding.location_details.buttons.next')}</span>
                    ) : (
                        <span>{__('onboarding.location_details.buttons.valid')}</span>
                    )}
                </Button>
            </div>
        </>
    );
}
